import React, { useState } from 'react';
import { formatCep } from '../../utils/viaCepApi';

interface StepAddressProps {
    formData: {
        cep: string;
        street: string;
        number: string;
        complement: string;
        neighborhood: string;
        city: string;
        state: string;
    };
    updateFormData: (data: Partial<StepAddressProps['formData']>) => void;
    onNext: () => void;
    onBack: () => void;
    searchCep: (cep: string) => Promise<void>;
    isLoadingCep: boolean;
    error: string | null;
}

const StepAddress: React.FC<StepAddressProps> = ({
    formData,
    updateFormData,
    onNext,
    onBack,
    searchCep,
    isLoadingCep,
    error
}) => {
    const [localCep, setLocalCep] = useState(formData.cep);

    const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatCep(e.target.value);
        setLocalCep(formatted);

        // Auto-search when CEP is complete
        const clean = e.target.value.replace(/\D/g, '');
        if (clean.length === 8) {
            searchCep(clean);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onNext();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="text-center mb-8">
                <p className="text-sm text-primary font-medium mb-2">Informações adicionais</p>
                <h2 className="text-2xl font-bold text-gray-900">Endereço do seu negócio</h2>
                <p className="text-gray-500 text-sm mt-2">Informe seu endereço para ser encontrado facilmente pelos clientes</p>
            </div>

            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                {/* CEP */}
                <div>
                    <div className="flex justify-between items-center mb-1">
                        <label htmlFor="cep" className="block text-sm font-medium text-gray-700">
                            CEP
                        </label>
                        <a
                            href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                        >
                            Não sei meu CEP
                        </a>
                    </div>
                    <div className="relative">
                        <input
                            id="cep"
                            type="text"
                            value={localCep}
                            onChange={handleCepChange}
                            maxLength={9}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="00000-000"
                        />
                        {isLoadingCep && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bairro e Logradouro */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 mb-1">
                            Bairro
                        </label>
                        <input
                            id="neighborhood"
                            type="text"
                            value={formData.neighborhood}
                            onChange={(e) => updateFormData({ neighborhood: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="Bairro"
                            readOnly={!!formData.neighborhood}
                        />
                    </div>
                    <div>
                        <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                            Logradouro
                        </label>
                        <input
                            id="street"
                            type="text"
                            value={formData.street}
                            onChange={(e) => updateFormData({ street: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="Rua, Avenida..."
                            readOnly={!!formData.street}
                        />
                    </div>
                </div>

                {/* Número e Complemento */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-1">
                            Número
                        </label>
                        <input
                            id="number"
                            type="text"
                            value={formData.number}
                            onChange={(e) => updateFormData({ number: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="123"
                        />
                    </div>
                    <div>
                        <label htmlFor="complement" className="block text-sm font-medium text-gray-700 mb-1">
                            Complemento
                        </label>
                        <input
                            id="complement"
                            type="text"
                            value={formData.complement}
                            onChange={(e) => updateFormData({ complement: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="Apt, Sala, Bloco..."
                        />
                    </div>
                </div>

                {/* Cidade e Estado (readonly) */}
                {formData.city && (
                    <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        📍 {formData.city} - {formData.state}
                    </div>
                )}
            </div>

            <button
                type="submit"
                className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all shadow-sm"
            >
                Continuar
            </button>
        </form>
    );
};

export default StepAddress;
